import { fg } from '@atlaskit/platform-feature-flags';
import { getThirdPartyFilterOptions } from '@atlassian/search-client/third-party-filter-options';

import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';

type ThirdPartyFilterOptionsResult = Awaited<ReturnType<typeof getThirdPartyFilterOptions>>;

/**
 * Module-level cache of in-flight (or resolved) promises for
 * `getThirdPartyFilterOptions`, keyed by `${cloudId}::${datasourceId}`.
 *
 * The endpoint returns **all** custom filters for an integration in a single
 * response, so multiple filters that share the same datasource (e.g. campaign
 * status, case status, deal stage) can reuse one promise instead of each
 * issuing an independent network request.
 *
 * Cache lifetime: a typical session has at most O(connected integrations)
 * entries (usually 1–5), so no eviction strategy is needed.  The cache is
 * intentionally kept for the lifetime of the page — filter-option data is
 * static per datasource within a search session.
 *
 * Rejection handling: failed entries are removed so subsequent callers can
 * retry rather than receive a permanently-stuck rejected promise.
 *
 * Cache key: `cloudId::datasourceId` (and when `rovo-fps-3p-ux-salesforce-add-org-id` is on,
 * `::orgId` is appended so different orgs do not share a cached response). `aggAbsoluteUrl` is
 * intentionally excluded — it is constant for a given (cloudId, datasourceId) pair within a
 * session and so cannot disambiguate entries.
 */
const cache = new Map<string, Promise<ThirdPartyFilterOptionsResult>>();

/**
 * Returns a (possibly cached) promise for `getThirdPartyFilterOptions` for the
 * given cloud + datasource combination.  The first caller for a key triggers
 * the network request; subsequent callers receive the same in-flight promise.
 *
 * Returns `null` synchronously-resolved when `cloudId` or `datasourceId` are
 * missing (matches the upstream contract — `getThirdPartyFilterOptions` itself
 * returns `null` on failure).  Callers should already guard against missing
 * config before invoking this function.
 *
 * On rejection the cache entry is removed so a subsequent call can retry.
 */
export const getCachedThirdPartyFilterOptions = (
	config: CloudConfig,
): Promise<ThirdPartyFilterOptionsResult> => {
	const { cloudId } = config;
	const datasourceId = config.selectedDatasource?.datasourceId;

	// Defensive guard: do not invoke (or cache) without a complete key.
	// Callers in filter index files are expected to short-circuit before this.
	if (!cloudId || !datasourceId) {
		return Promise.resolve(null);
	}

	const passOrgId = fg('rovo-fps-3p-ux-salesforce-add-org-id');
	const orgId = passOrgId ? config.orgId : undefined;
	const orgSegment = passOrgId ? (config.orgId ?? '') : '';

	const fetchOptions = {
		cloudId,
		datasourceId,
		orgId,
		aggAbsoluteUrl: config.aggAbsoluteUrl,
	};

	// Feature gate: when off, every caller fetches directly (no shared state,
	// one network request per caller).  When on, callers share a single
	// in-flight promise per datasource via the cache below.
	if (!fg('rovo-fps-3p-ux-sf-shared-custom-filters')) {
		return getThirdPartyFilterOptions(fetchOptions);
	}

	const key = `${cloudId}::${datasourceId}::${orgSegment}`;

	let promise = cache.get(key);
	if (!promise) {
		promise = getThirdPartyFilterOptions(fetchOptions);
		// Evict on failure so the next call can retry.
		promise.catch(() => cache.delete(key));
		cache.set(key, promise);
	}

	return promise;
};

/**
 * Clears the entire cache.  Intended for use in tests only.
 */
export const clearThirdPartyFilterOptionsCache = (): void => {
	cache.clear();
};
